// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Where a device *is*, as points that can be read and written (M9-01, 11 §3.1).
//
// Every device kind in 04 §7 says where it stands in its own vocabulary: a flipper has a `pivot`, a
// pop bumper a `centre`, a slingshot an `a` and a `b`, a rollover a `region` that is either a circle
// or a rectangle. That is right for the format — a flipper's pivot is not the same *kind* of fact as
// a slingshot's endpoint, and flattening them into `x, y` would lose which is which — and it is
// exactly wrong for a canvas, where the only question is "what did the pointer just grab, and where
// has it been dragged to".
//
// So this is the one place that knows both. It answers a device as a list of **named handles**, and
// takes a handle and a point back. Nothing else in the Builder needs a `switch` over sixteen kinds,
// which matters because a seventeenth kind that this file does not know about is a compile error
// here and nowhere else.
//
// **Read and write are deliberately separate, and writing never invents a field.** `withAnchor`
// rebuilds the device it was given with one point replaced and everything else — strength, hold
// time, exit direction, the id — carried across untouched. An author who drags a flipper two
// millimetres must not discover that their return torque has been reset to a default.
//
// **`dropTargetBank` has no anchors and that is not an omission.** A bank is a list of target ids;
// its position is wherever its targets are. Dragging one would be dragging a set, which is a
// multi-selection and belongs to whatever moves several devices at once — not to a phantom point
// this file would have to invent a location for.
//
// Pure arithmetic over the definition, in a file that never runs during a simulation step: no
// transcendentals, no clock, no allocation that matters.

import type {
  MachineDevice,
  Point2,
  Region,
} from './types'

/**
 * A grabbable point on a device.
 *
 * `role` is the field it came from, spelled as 04 spells it, so an inspector can label a handle
 * with the word the format uses rather than a word the Builder made up.
 */
export interface DeviceAnchor {
  readonly role: string
  readonly at: Point2
  /**
   * Whether dragging this handle moves the whole device or reshapes it.
   *
   * A flipper's pivot **is** the flipper's position; a slingshot's `a` is one end of a rubber whose
   * other end stays put. Two different gestures that would look identical without this: one slides,
   * one stretches.
   */
  readonly shapes: boolean
}

/**
 * A coordinate with binary representation noise taken off it.
 *
 * `-0.16 + 0.02` is `-0.14000000000000001` in binary64, and a definition full of those is a file
 * that changes when nothing did and a diff nobody can read. Seven decimal places is a tenth of a
 * micrometre — four orders finer than the 1 mm design grid, finer than any real playfield is drilled
 * — so nothing an author can express is lost, and every sum that should land on a round number does.
 *
 * **This is not a simulation value.** Nothing in `sim/` reads a definition mid-step; the loader turns
 * one into colliders once, before any ball moves. Rounding here changes what is *authored*, never
 * what is computed from it, so it cannot make two clients disagree about a game.
 */
function tidy(value: number): number {
  return Number(value.toFixed(7))
}

/** Where a rollover's region sits, as a point. Circles by centre, rectangles by their middle. */
function regionCentre(region: Region): Point2 {
  if (region.shape === 'circle') return region.centre
  return [(region.min[0] + region.max[0]) / 2, (region.min[1] + region.max[1]) / 2]
}

function moveRegion(region: Region, to: Point2): Region {
  if (region.shape === 'circle') return { ...region, centre: to }
  const [cx, cy] = regionCentre(region)
  const dx = to[0] - cx
  const dy = to[1] - cy
  return {
    ...region,
    min: [tidy(region.min[0] + dx), tidy(region.min[1] + dy)],
    max: [tidy(region.max[0] + dx), tidy(region.max[1] + dy)],
  }
}

/**
 * Every point of this device an author can take hold of.
 *
 * In a fixed order per kind, because the canvas draws handles in the order it is given them and a
 * list that reordered itself between frames would be handles that swap under a fingertip.
 */
export function deviceAnchors(device: MachineDevice): readonly DeviceAnchor[] {
  switch (device.kind) {
    case 'flipper':
      return [{ role: 'pivot', at: device.pivot, shapes: false }]
    case 'popBumper':
      return [{ role: 'centre', at: device.centre, shapes: false }]
    case 'magnet':
      return [{ role: 'centre', at: device.centre, shapes: false }]
    case 'captiveBall':
      return [{ role: 'centre', at: device.centre, shapes: false }]
    case 'kicker':
    case 'plunger':
    case 'autoPlunger':
      return [{ role: 'centre', at: device.centre, shapes: false }]
    case 'rollover':
      return [{ role: 'region', at: regionCentre(device.region), shapes: false }]
    case 'slingshot':
    case 'standupTarget':
    case 'dropTarget':
    case 'spinner':
    case 'gate':
    case 'ballSaveGate':
    case 'diverter':
      return [
        { role: 'a', at: device.a, shapes: true },
        { role: 'b', at: device.b, shapes: true },
      ]
    case 'dropTargetBank':
      // A set of targets, positioned by its members. See the note at the top of this file.
      return []
  }
}

/**
 * The unit normal to a face, on the side the old one pointed.
 *
 * **A gate carries a `passDirection` that the validator requires to be perpendicular to its own
 * face**, so dragging one end of a gate would otherwise leave a device that is invalid the moment
 * it is let go — and the failure is not visible on a canvas, because a gate looks exactly the same
 * whichever way it lets a ball through.
 *
 * Which of the two perpendiculars is kept is decided by the one already there: a diverter that sent
 * the ball right must go on sending it right after being lengthened. Only `√` and arithmetic, so
 * this stays inside the core's own rules.
 */
function normalTo(a: Point2, b: Point2, was: Point2): Point2 {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const length = Math.sqrt(dx * dx + dy * dy)
  // A face with no direction has no normal. Left as it was rather than made up: the validator
  // reports the degenerate face, which is the actual fault.
  if (length === 0) return was
  const nx = -dy / length
  const ny = dx / length
  const sameSide = nx * was[0] + ny * was[1] >= 0
  return [tidy(sameSide ? nx : -nx), tidy(sameSide ? ny : -ny)]
}

/**
 * The same device with one handle moved.
 *
 * Answers the device unchanged for a role it does not have, rather than throwing: the caller is a
 * pointer event, and a drag that arrives naming a handle the selection no longer holds is an
 * ordinary consequence of a selection changing mid-gesture.
 */
export function withAnchor(device: MachineDevice, role: string, to: Point2): MachineDevice {
  switch (device.kind) {
    case 'flipper':
      return role === 'pivot' ? { ...device, pivot: to } : device
    case 'popBumper':
    case 'magnet':
    case 'captiveBall':
    case 'kicker':
    case 'plunger':
    case 'autoPlunger':
      return role === 'centre' ? { ...device, centre: to } : device
    case 'rollover':
      return role === 'region' ? { ...device, region: moveRegion(device.region, to) } : device
    case 'slingshot':
    case 'standupTarget':
    case 'dropTarget':
    case 'spinner':
      if (role === 'a') return { ...device, a: to }
      if (role === 'b') return { ...device, b: to }
      return device
    case 'gate':
    case 'ballSaveGate':
    case 'diverter': {
      // **The face moves and the pass direction follows it**, kept apart from the other two-ended
      // kinds precisely because they have no such field: 04 §7 lists the members each kind may
      // carry and the validator refuses one it does not recognise, so adding a `passDirection` to a
      // slingshot would make a dragged slingshot unpublishable.
      if (role === 'a') {
        return { ...device, a: to, passDirection: normalTo(to, device.b, device.passDirection) }
      }
      if (role === 'b') {
        return { ...device, b: to, passDirection: normalTo(device.a, to, device.passDirection) }
      }
      return device
    }
    case 'dropTargetBank':
      return device
  }
}

/**
 * The whole device, shifted.
 *
 * Every anchor moves by the same delta, including the ones that reshape — which is what makes this
 * a *move* rather than a stretch. Built on `withAnchor` so a new device kind is taught its geometry
 * once.
 */
export function moveDevice(device: MachineDevice, dx: number, dy: number): MachineDevice {
  let moved = device
  for (const anchor of deviceAnchors(device)) {
    moved = withAnchor(moved, anchor.role, [tidy(anchor.at[0] + dx), tidy(anchor.at[1] + dy)])
  }
  return moved
}

/**
 * Where the device sits as a single point, for anything that needs one — a label, a selection
 * marker, a distance comparison.
 *
 * The mean of its anchors, which for a one-anchor device is that anchor and for a two-ended one is
 * the middle of the rubber. `undefined` for a device with no anchors at all, because the honest
 * answer for a drop-target bank is "ask its targets" and a made-up origin would be drawn somewhere
 * a player could click.
 */
export function devicePosition(device: MachineDevice): Point2 | undefined {
  const anchors = deviceAnchors(device)
  if (anchors.length === 0) return undefined
  let x = 0
  let y = 0
  for (const anchor of anchors) {
    x += anchor.at[0]
    y += anchor.at[1]
  }
  return [x / anchors.length, y / anchors.length]
}
